import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ThumbsUp, Radio } from "lucide-react";

const FEED_POOL = [
  "ZONE A-114 · SHADE CONFIRMED · BAY 3 ROW N",
  "ZONE C-042 · SHADOW DECAYING · 12 MIN LEFT",
  "ZONE K-207 · CANOPY HOLDING · PICNIC LAWN",
  "ZONE B-088 · SUN SPOT PRIME · GOLDEN 94%",
  "ZONE F-153 · TREE-LINE SHADE · PITCH 7",
  "ZONE D-031 · NEW ZONE MAPPED · AWAITING VOTES",
];

const MARKERS = [
  { top: "18%", left: "22%", zone: "A-114" },
  { top: "42%", left: "64%", zone: "C-042" },
  { top: "66%", left: "34%", zone: "K-207" },
  { top: "30%", left: "82%", zone: "F-153" },
  { top: "74%", left: "72%", zone: "B-088" },
];

export default function Engine() {
  const [feed, setFeed] = useState(FEED_POOL.slice(0, 3));
  const [votes, setVotes] = useState({ "A-114": 128, "C-042": 64, "K-207": 41 });
  const idx = useRef(3);

  useEffect(() => {
    const t = setInterval(() => {
      setFeed((f) => [FEED_POOL[idx.current % FEED_POOL.length], ...f].slice(0, 5));
      idx.current += 1;
    }, 3600);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      id="engine"
      data-testid="engine-section"
      className="border-b border-[var(--sp-line)]"
    >
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:py-28">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mb-4 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sun)]"
          >
            <span className="h-px w-10 bg-[var(--sun)]" />
            Coming Soon // 05
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-2xl font-extrabold uppercase leading-none tracking-tight sm:text-4xl lg:text-5xl"
          >
            Crowdsourced comfort, validated in real time
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-[var(--sp-muted)]"
          >
            Our upcoming reporting engine turns every visitor into a sensor. Map a comfort zone in
            one tap, validate someone else's report, and watch shadow decay timelines update live —
            all without an account.
          </motion.p>
          <ul className="mt-8 space-y-4">
            {[
              "One-tap zone mapping from any device",
              "Community validation voting on every report",
              "Live shadow-decay countdowns per zone",
            ].map((f, i) => (
              <motion.li
                key={f}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-4 border-l-2 border-[var(--sun)] pl-4 text-sm text-[var(--sp-fg)] sm:text-base"
              >
                {f}
              </motion.li>
            ))}
          </ul>
          <p className="mt-8 font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-faint)]">
            Live panel is a simulated preview of the upcoming engine
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          data-testid="engine-map-panel"
          className="border border-[var(--sp-line)] bg-[var(--sp-surface)]"
        >
          <div className="flex items-center justify-between border-b border-[var(--sp-line)] px-5 py-3">
            <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sp-muted)]">
              <Radio className="h-3.5 w-3.5 text-[var(--sun)]" />
              Comfort-Zone Grid · Sector 7
            </span>
            <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.14em] text-[var(--sun)]">
              <span className="sp-blink h-1.5 w-1.5 bg-[var(--sun)]" />
              Live
            </span>
          </div>
          <div className="map-grid relative h-64 overflow-hidden sm:h-72">
            {MARKERS.map((m) => (
              <div key={m.zone} className="absolute" style={{ top: m.top, left: m.left }}>
                <span className="relative flex h-3 w-3">
                  <span className="sp-ping absolute inline-flex h-full w-full bg-[var(--sun)]" />
                  <span className="relative inline-flex h-3 w-3 rotate-45 border border-black bg-[var(--sun)]" />
                </span>
                <span className="mt-1 block font-mono2 text-[10px] uppercase tracking-[0.1em] text-[var(--sp-muted)]">
                  {m.zone}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--sp-line)]">
            <ul data-testid="engine-live-feed" className="divide-y divide-[var(--sp-line)]">
              {feed.map((item, i) => (
                <li
                  key={`${item}-${i}`}
                  className={`flex items-center gap-3 px-5 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.1em] sm:text-[11px] ${
                    i === 0 ? "text-[var(--sun)]" : "text-[var(--sp-muted)]"
                  }`}
                >
                  <MapPin className="h-3 w-3 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-3 divide-x divide-[var(--sp-line)] border-t border-[var(--sp-line)]">
            {Object.entries(votes).map(([zone, count]) => (
              <button
                key={zone}
                type="button"
                data-testid={`validate-zone-${zone.toLowerCase()}-button`}
                onClick={() => setVotes((v) => ({ ...v, [zone]: v[zone] + 1 }))}
                className="group flex flex-col items-center gap-1 px-3 py-4 transition-colors duration-300 hover:bg-[var(--sun)]"
              >
                <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.1em] text-[var(--sp-muted)] group-hover:text-black">
                  <ThumbsUp className="h-3 w-3" />
                  Validate {zone}
                </span>
                <span className="font-mono2 text-sm font-bold text-[var(--sun)] group-hover:text-black">
                  {count}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
